// Specific-action suggestion for the Sensei update card on Home.
// Replaces the previous generic "nudge up gradually" line that the
// user flagged as non-actionable. Deterministic — no AI — so the
// advice stays consistent and doesn't burn the spend guard.
//
// Scope:
//  - salinity: compute delta in ppt, suggest a water change of a
//    specific size + salt-mix target. Uses tank.volume_water_gal
//    when present to give the answer in real numbers; falls back
//    to a percentage when not.
//  - alk / calcium / magnesium (the DOSABLE set, tank param keys):
//    point the user at the in-app Dose calculator since that path is
//    the authoritative place to compute mL. Direction-aware (never
//    "dose more" when a value is HIGH) and product-aware: if the
//    keeper doses an all-in-one (e.g. All-For-Reef, which moves alk
//    AND Ca together), Sensei names a single-parameter alternative so
//    they can lift one number without dragging the other.
//  - temp / ph / nitrate / phosphate: each has its own real reefer
//    advice (water changes don't lower temp, and they don't fix pH
//    either — the old "let it drift back with a water change" generic
//    was wrong reef advice and a user flagged it), and its own
//    freshwater advice on a freshwater tank (feedback #74).

// Tank param keys, NOT the dose API's alk/ca/mg — the Home selector
// passes the tank key. Using the dose keys here meant an off-band
// Calcium or Magnesium fell through to the generic copy.
import { trendClause } from './trend.js';
import { paramLabelLower } from './param-labels.js';
import { FRESHWATER_SUBTYPES } from './param-explainers.js';
import { tankSubtype } from './tank-subtype.js';
import { isGradedParam } from './tank-purpose.js';

const DOSABLE = new Set(['alk', 'calcium', 'magnesium']);

// All-in-one products that move more than one parameter. When one of
// these is the keeper's saved product and the LOW parameter can be
// raised on its own another way, Sensei points at the alternative.
// Mirror of the multi-effect products in server/dose-calc.js.
const MULTI_PARAM_PRODUCTS = {
  tm_all_for_reef: { label: 'All-For-Reef', raises: 'alkalinity and calcium together' },
};

// Display labels for saved single-parameter products, so the copy can
// name the product the keeper actually uses.
const PRODUCT_LABELS = {
  brs_soda_ash: 'BRS Soda Ash',
  baking_soda: 'the baking soda option',
  brs_calcium_chloride: 'BRS Calcium Chloride',
  brs_magnesium: 'BRS Magnesium',
  tm_all_for_reef: 'All-For-Reef',
  esv_bionic_alk: 'ESV B-Ionic Alkalinity',
  esv_bionic_ca: 'ESV B-Ionic Calcium',
  esv_magnesium: 'ESV B-Ionic Magnesium',
};

// Round to one decimal — output reads like a refractometer.
function round1(n) {
  return Math.round(n * 10) / 10;
}

// Salinity amounts in the keeper's unit. The advice is worked out in ppt
// (storage); PSU is the same number under its own name, and an S.G.
// keeper also gets the S.G. step, since a refractometer on that scale is
// what they will read it on.
// A step under 0.001 S.G. is finer than a refractometer reads, so it says
// so instead of quoting "0.0001 S.G." as if the keeper could see it.
function salinityAmount(ppt, unit) {
  if (unit === 'psu') return `${ppt} PSU`;
  if (unit === 'sg') {
    const sg = (n) => n / (35 / 0.026);
    const [a, b] = String(ppt).replace(/^~/, '').split('–').map(Number);
    const hi = sg(b != null ? b : a);
    if (!(hi >= 0.0005)) return `${ppt} ppt (under 0.001 S.G., finer than a refractometer reads)`;
    const f = (n) => Number(sg(n).toFixed(3)).toString();
    return b != null && f(a) !== f(b)
      ? `${ppt} ppt (about ${f(a)}–${f(b)} S.G.)`
      : `${ppt} ppt (about ${f(b != null ? b : a)} S.G.)`;
  }
  return `${ppt} ppt`;
}

function salinityAction(latest, p, trend, _tank, unit = 'ppt') {
  const amt = (ppt) => salinityAmount(ppt, unit);
  // _tank is reserved — future versions will scale the % water change
  // suggestion by tank.volume_water_gal so the user gets gallons, not
  // percentages. v1 keeps the copy unit-free.
  const target = Number(p?.target) || 35;
  const delta = round1(Math.abs(latest - target));
  // Mix targets — salt mixes are commonly ~36 ppt for top-up + ~33
  // for dilution. These are typical home values; close enough for
  // an actionable hint without the user pulling out a calculator.
  // Low-side fixes must attack the mechanism (top-off water carries the
  // salt in), not lean on dilution math: a 5% change with mix only
  // ~1-2 ppt above the tank moves salinity ~0.1 ppt — below what a
  // refractometer resolves (review finding: the old recipes could not
  // close the deficits they were prescribed for).
  if (trend === 'low') {
    if (delta < 0.4) {
      return 'Salt the next top-off lightly and retest in 48 h — small drift, no water change yet.';
    }
    if (delta < 1.2) {
      return `Salt your top-off water for the next few days (aim the tank up ~${amt('0.2')} per day) and retest daily. A small water change barely moves salinity — the top-off is what carries salt in.`;
    }
    return `Salt every top-off until you’re back on target, ~${amt('0.2–0.3')} per day — slow is safer than fast. If you’d rather change water, it takes 15–20% changes with mix ~${amt('4')} above target, repeated over several days, to move this much.`;
  }
  // high
  if (delta < 0.4) {
    return 'Top off with RO only for a few days; salinity rises when evaporation outpaces top-up.';
  }
  // Dilution math: replacing tank water with a lower-salinity MIX
  // barely moves the number (a 5% change at 33 ppt shifts a 36.5 ppt
  // tank ~0.2 ppt). Plain RO is the real tool: each 1% of volume
  // replaced with RO drops salinity ~0.35 ppt at reef levels.
  if (delta < 1.2) {
    // No parentheses around the amount: an S.G. keeper's amount carries its
    // own, and the copy ended "reads))".
    return `Remove ~1% of tank volume and replace it with plain RO/DI, which drops salinity ~${amt('0.35')}. Retest before repeating. Skipping salt in top-offs also works, just slower.`;
  }
  return `Remove 1–2% of tank volume a day and replace it with plain RO/DI, lowering salinity ~${amt('0.35–0.7')} per day, until you are back on target. Slow is safer than fast.`;
}

// Format a one-decimal mL value without a trailing ".0" — so "28" stays
// "28" and "27.5" stays "27.5". Matches the forecast clause's rounding.
function fmtMl(n) {
  if (!Number.isFinite(n)) return null;
  return Number(n).toFixed(1).replace(/\.0$/, '');
}

// Compute the suggested next rate when consumption has run ahead of the
// schedule: +15% with a 0.5 mL floor so the bump is meaningful on small
// tanks. Precision tracks current rate — at 5 mL/day and up reefers tune
// in half-mL steps; below that, half-mL rounding lands BELOW the floor
// (0.6 → 1.1 floor → Math.round(2.2)/2 = 1.0, a 0.4 mL bump) so we keep
// 0.1 mL precision for the small-head range where DOS heads are
// calibrated anyway.
function bumpDoseRate(current) {
  const target = current * 1.15;
  const bumped = target < current + 0.5 ? current + 0.5 : target;
  return current < 5 ? Math.round(bumped * 10) / 10 : Math.round(bumped * 2) / 2;
}

// Evidence that the keeper actually doses this tank param: an active
// DOS head, a manual dose logged in the last 30 days (dosing_activity,
// server-normalised to tank keys), or a stocked supplement in the
// matching supply category. Without evidence, "pause your dosing" is
// advice about a dose that doesn't exist (user-reported on high pH).
const SUPPLY_CATEGORY = { alk: 'alkalinity', calcium: 'calcium', magnesium: 'magnesium' };

function dosesParam(tank, paramKey) {
  if (Number(tank?.dose_summary?.[paramKey]?.ml_24h) > 0) return true;
  if (Number(tank?.dosing_activity?.[paramKey]?.doses) > 0) return true;
  const cat = SUPPLY_CATEGORY[paramKey];
  if (!cat) return false;
  return (tank?.supplies || []).some((s) => (s.category || '').toLowerCase() === cat);
}

function dosableAction(paramKey, trend, p, dose, dosesIt) {
  const label = paramLabelLower(paramKey);
  const observedMl = Number(dose?.ml_24h);
  const hasObservedDose = Number.isFinite(observedMl) && observedMl > 0;

  // Dosing only ever ADDS — pointing a HIGH reading at the Dose
  // calculator is advice in the wrong direction (and a wrong dose
  // can kill a tank). High readings come down by pausing doses +
  // letting consumption / water changes do the work.
  if (trend === 'high') {
    if (hasObservedDose) {
      const rate = fmtMl(observedMl);
      return `Your DOS dosed ~${rate} mL of ${label} in the last 24 h — pause the schedule (or cut it in half) and retest in 24 h. Let consumption pull it back into range. A 10% water change helps if you're more than a point over.`;
    }
    if (dosesIt) {
      return `Pause ${label} dosing and retest in 24 h — let consumption bring it down. A 10% water change speeds it up if you're more than a point over.`;
    }
    // No dosing anywhere in the log — there's nothing to pause. High
    // readings on an undosed tank are usually the test or the salt.
    return `You're not dosing ${label}, so there's nothing to pause — retest first (fresh reagents or a second kit) before acting. If it's real, it likely came in with your salt mix: hold off on water changes for a week or two and let consumption bring it down.`;
  }

  // LOW with an all-in-one product: the poster's exact case. All-For-Reef
  // can't lift alkalinity without also pushing calcium, so steer them to
  // the single-parameter alternative.
  const product = p?.product || null;
  const multi = product ? MULTI_PARAM_PRODUCTS[product] : null;
  if (paramKey === 'alk' && multi) {
    return `You dose ${multi.label}, which raises ${multi.raises}. To lift alkalinity on its own, tap DOSE on the alkalinity card and switch the product to the baking soda option. Sensei computes a safe mL and caps the rise at 1.4 dKH per day.`;
  }

  // LOW with an active DOS: name the observed rate and suggest a
  // concrete bump. Far more useful than a generic "tap DOSE" when the
  // reefer's already automated — they want to know "what should the
  // schedule be tomorrow," not "open a calculator." Honest about the
  // mechanism: consumption rose, not "you didn't dose enough."
  if (hasObservedDose) {
    const rate = fmtMl(observedMl);
    const nextRate = fmtMl(bumpDoseRate(observedMl));
    return `Your DOS dosed ~${rate} mL of ${label} in the last 24 h but ${label} is still drifting low — consumption has likely risen. Lift the schedule about 15% (to ~${nextRate} mL/day) and retest in 48 h before adjusting again.`;
  }

  // LOW with a saved single-parameter product: name it in the copy.
  if (product && PRODUCT_LABELS[product] && !multi) {
    return `Tap DOSE on the ${label} card — Sensei calculates the exact mL of ${PRODUCT_LABELS[product]} based on your working volume.`;
  }
  return `Tap DOSE on the ${label} card — Sensei calculates the exact mL based on your working volume.`;
}

// Temperature is an ENERGY-balance problem, not a chemistry one — water
// changes barely move it (you'd need a huge volume of much-cooler new
// water to drop a single degree). Real reefer fixes are evaporative
// cooling on HIGH and heater diagnosis on LOW.
function tempAction(_latest, _p, status) {
  if (status === 'low') {
    return 'Check the heater first — wrong setpoint, unplugged, or a failed element is almost always the cause. Once the tank warms back, retest in 12 h before changing anything else.';
  }
  return `Aim a clip fan across the sump or display surface — evaporative cooling drops a degree or two within the hour. ${HEATER_STUCK} Shorten the photoperiod if a heatwave is the cause.`;
}

// pH is driven by CO2 + alkalinity, not by water changes. Most "low pH"
// reads are room-air problems (closed windows, winter); most "high pH"
// reads are kalkwasser overshoot — but ONLY on tanks that dose. On an
// undosed tank a high read is almost always timing (peak-photoperiod)
// or the probe/kit, so telling that keeper to "pause dosing" was
// advice about a dose that doesn't exist (user-reported).
function phAction(_latest, _p, status, tank) {
  if (status === 'low') {
    return 'Low pH is almost always high CO2 in the room (winter, windows closed). Open a window near the tank, or pipe outside air to the skimmer intake. Confirm alkalinity is in range — low alk pulls pH down with it.';
  }
  if (dosesParam(tank, 'alk')) {
    return 'Pause any kalkwasser or hydroxide-based alk dosing and retest in 24 h. Also try a read first thing in the morning — pH naturally cycles 0.2–0.3 across the day, so a peak-photoperiod read can read high without anything being wrong.';
  }
  return "You're not dosing anything that pushes pH, so a high read here is usually the reading, not the tank — pH cycles 0.2–0.3 through the day and peaks late in the photoperiod. Retest first thing in the morning and recalibrate the probe (or use a fresh kit). If it still reads high, confirm alkalinity is in range; pH holding 8.3–8.5 with normal alk and happy livestock isn't a problem to chase, but a confirmed 8.6+ is worth investigating (kalk in the top-off reservoir is the usual culprit).";
}

// A pH inside the keeper's range but under (or over) its target. The card
// read "0.25 under", nothing on Home said how to raise it, and a keeper
// asked in a feedback ticket (#71, 2026-10-15). Inside the range nothing
// needs fixing, so the answer says that first, then only what the off-band
// copy here and the pH explainer already say. pH alone: other params'
// off-band copy names numbers (phosphate "below 0.02 ppm") that are untrue
// for a reading inside the range.
export function inRangeAction(paramKey, direction, tank) {
  if (paramKey !== 'ph' || (direction !== 'low' && direction !== 'high')) return null;
  if (isFreshwater(tank)) return freshPhInRange(direction, tank);
  const lead = 'This is inside your range, so nothing needs fixing.';
  if (direction === 'low') {
    // A probe reads under target every night, and so does a kit test at
    // 10 pm: that dip is normal, so it comes before any advice. Alkalinity
    // only where its card is shown and graded (not a QT, FOWLR or a cycle).
    const alk = alkCounts(tank)
      ? ' Keep alkalinity in your range too: it is the buffer that keeps pH from falling.'
      : '';
    return `${lead} pH falls at night and reads lowest first thing in the morning, because carbon dioxide builds up overnight. If it stays under your target through the day, give the tank fresher air: carbon dioxide in the room pulls pH down, so open a window near the tank, or pipe outside air to the skimmer intake.${alk}`;
  }
  // An undosed reef has nothing to lower an in-range pH with: it peaks late
  // in the day by itself, as the explainer above already says.
  if (!dosesParam(tank, 'alk')) return null;
  // A probe or a 6 pm kit test reads over target every afternoon: the peak
  // comes first, as the dip does above.
  return `${lead} pH peaks late in the day while the lights are on. If it still reads over your target first thing in the morning, kalkwasser and hydroxide-based alkalinity products raise pH, so if you dose one, that is the first thing to look at.`;
}

function alkCounts(tank) {
  if (!tank?.params || !Object.keys(tank.params).length) return true;
  const alk = tank.params.alk;
  return !!alk && !alk.hidden && isGradedParam(tank, 'alk');
}

function freshPhInRange(direction, tank) {
  const slug = tankSubtype(tank);
  const lead = 'This is inside your range, so nothing needs fixing.';
  const sameTime = PLANTED.has(slug)
    ? ' If you inject CO2, pH falls while it runs and rises when it stops, so test at the same time each day.'
    : '';
  if (direction === 'low') {
    if (slug === 'cichlid') {
      return `${lead} To raise it, test KH too: African cichlids want hard, alkaline water, and soft water lets pH fall. Crushed coral or limestone rock holds pH up; driftwood, leaves and peat pull it down. Water changes raise it only if the new water is harder than the tank. Change it slowly.`;
    }
    return `${lead} To raise it, test KH too, since low KH lets pH fall. Driftwood, leaves and peat lower pH, mostly in soft water, and so does injected CO2. Water changes raise it only if your tap water reads higher, so test that too. Change it slowly.${sameTime}`;
  }
  if (slug === 'cichlid') {
    return `${lead} To lower it, if you add a buffer or cichlid salt, check you are following its label, since more raises pH further. Test your tap water too: it sets the starting point. Change it slowly.`;
  }
  return `${lead} To lower it, test your tap water first: it sets the starting point. Rock such as limestone, or crushed coral or shells in the gravel, raises pH, so if your ${animals(tank)} want it lower, take some out, a little at a time.${sameTime}`;
}

// Ammonia and nitrite have no LOW: their target is zero. Practice, not a
// number and never a product: dilute with a water change, feed less, make
// sure the filter is running, test again soon. Matters most in a QT, where
// a fish sits in a small, often new, tank.
function wasteAction(paramKey, status, tank) {
  if (status === 'low') return 'Zero is the target here, so there is nothing to raise.';
  // Nitrite harms saltwater fish far less than freshwater fish, so it is
  // named as a sign of the filter, not as a poison.
  // In fresh water it is a poison in its own right (the freshwater
  // "What is this?" copy says so too), feedback #74 review.
  const lead =
    paramKey === 'ammonia'
      ? 'Ammonia is toxic to fish.'
      : isFreshwater(tank)
        ? 'Nitrite is toxic to freshwater fish, even at low levels, and shows the filter is not keeping up.'
        : 'Nitrite shows the filter is not keeping up with the waste.';
  // A water change dilutes a medication too; the label says how to handle it.
  const treatment =
    tank?.purpose === 'quarantine'
      ? ' If a treatment is running, check its label about water changes first.'
      : '';
  return `${lead} Keepers usually change some water with new water matched to the tank, feed less, check the filter is running, and test again within a day.${treatment}`;
}

// Nitrate water changes are LEGITIMATE on HIGH (the old fallback was at
// least directionally OK here), but the LOW case is real and the old
// "nudge up" copy did nothing — zero nitrate starves corals, especially SPS.
function nitrateAction(_latest, _p, status) {
  if (status === 'low') {
    return 'Feed a little more — nitrate at zero starves zooxanthellae and pales corals (SPS especially). If it stays at zero, dose a small amount (Brightwell NeoNitro or a dilute KNO3 stock) and back off any aggressive nitrogen export (biopellets, carbon dosing, heavy refugium harvest).';
  }
  return 'A 15–20% water change with fresh salt mix pulls it down fastest. Then audit feeding (a single extra pinch a day adds up) and check skimmer wetness — a dry skim means nutrients accumulate.';
}

// Phosphate has a similar shape but the tools are different: GFO is the
// standard high-side tool, KH2PO4/NeoPhos the low-side dose.
function phosphateAction(_latest, _p, status) {
  if (status === 'low') {
    return 'Feed a little more — phosphate below 0.02 ppm starves zooxanthellae and pales corals. If GFO is running, pause it; otherwise dose a small amount of NeoPhos or a dilute KH2PO4 stock.';
  }
  return 'Run GFO (granular ferric oxide) in a reactor or media bag — it’s the standard tool for phosphate. Pair with a 15% water change and a feeding audit; most high-PO4 reefs are just being fed a little too generously.';
}

// Freshwater (feedback #74, 2026-10-13): everything above is reef advice,
// and a community tank with high nitrate was told to use salt mix and check
// its skimmer. Practice only, no product and no number: the keeper's own
// range says HIGH or LOW. Built on the freshwater "What is this?" copy in
// param-explainers.js, which was checked against published sources.
const PLANTED = new Set(['planted-high', 'planted-low']);
const SHRIMP = 'shrimp';
// A powered heater is never touched in the water: its light (or the
// controller it runs from) says whether it is heating (fact-check,
// 2026-10-13: maker manuals say unplug first).
const HEATER_STUCK =
  'Check the heater: if its light or its controller shows it heating while the water is too warm, unplug it, since it may be stuck on.';

// Same rule as the app's isFreshwaterTank and the score: a tank made before
// the taxonomy has only its type label.
export function isFreshwater(tank) {
  return FRESHWATER_SUBTYPES.includes(String(tankSubtype(tank) || ''));
}

function animals(tank) {
  return tankSubtype(tank) === SHRIMP ? 'fish and shrimp' : 'fish';
}

function freshTempAction(status) {
  if (status === 'low') {
    return 'Check the heater first: is it plugged in, set to the right temperature, and does its light come on? A failed heater is a common cause, and a cold room or a power cut can chill a tank too. Warm it back slowly, never by adding hot water, and retest once it has warmed.';
  }
  return `Aim a fan across the water surface: evaporation cools the tank. ${HEATER_STUCK} In a heatwave, keep the lights off for a while. Warm water holds less oxygen, so add an air stone or ripple the surface, and cool the tank gradually, never with ice straight in the water.`;
}

// A tank that has drifted low for long can hold ammonia that a sudden rise
// in pH turns more harmful, so the water change is gradual.
const DRIFTED_FAR =
  'If pH has drifted far, test ammonia first and change a little at a time over several days: a sudden rise in pH makes any ammonia more harmful.';

function freshPhAction(status, tank) {
  const slug = tankSubtype(tank);
  const planted = PLANTED.has(slug);
  // Plants alone swing pH between day and night, and injected CO2 more so.
  const sameTime = planted
    ? ' If you inject CO2, pH falls while it runs and rises when it stops, so test at the same time each day.'
    : '';
  if (status === 'low') {
    // Water changes raise pH only with harder, more alkaline new water.
    if (slug === 'cichlid') {
      return `Retest, and test KH too: African cichlids want hard, alkaline water, and soft water lets pH fall. Crushed coral or limestone rock holds pH up; driftwood, leaves and peat pull it down. Water changes bring it back only if the new water is harder than the tank, so buffer it the way you usually do, following its label. ${DRIFTED_FAR}`;
    }
    const lead = 'Retest, and test KH too, since low KH lets pH fall.';
    const causes = planted
      ? 'Driftwood, leaves and peat lower pH, mostly in soft water.'
      : 'Driftwood, leaves and peat lower pH, mostly in soft water, and so does injected CO2.';
    const changes =
      'The water also turns slowly more acidic as waste breaks down. Test your tap water too: water changes bring pH back only if it reads higher.';
    const gasp =
      'If you inject CO2 and fish gasp at the surface, turn the CO2 down and add surface movement: too much CO2 harms fish.';
    // A high-tech planted tank most likely runs CO2, and too much of it
    // suffocates fish, so that check comes first (and is the push).
    if (slug === 'planted-high')
      return `${gasp} ${lead} ${causes} ${changes} ${DRIFTED_FAR}${sameTime}`;
    if (planted) return `${lead} ${gasp} ${causes} ${changes} ${DRIFTED_FAR}${sameTime}`;
    return `${lead} ${causes} ${changes} ${DRIFTED_FAR}`;
  }
  const ammonia = `Check ammonia reads zero too: ammonia harms ${animals(tank)} more at high pH.`;
  const steady = `Change it slowly: ${animals(tank)} handle a steady pH better than a sudden change.`;
  if (slug === 'cichlid') {
    // Their rockwork holds pH up on purpose; a buffer or salt is the usual
    // thing to look at.
    return `Retest, then test your tap water too: it sets the starting point. If you add a buffer or cichlid salt, check you are following its label, since more raises pH further. ${ammonia} ${steady}`;
  }
  const runOut = planted ? ' If you inject CO2, check it has not run out.' : '';
  return `Retest, then test your tap water too: it sets the starting point. Rock such as limestone, or crushed coral or shells in the gravel, raises pH, so take some out, a little at a time, if your ${animals(tank)} want it lower.${runOut} ${ammonia} ${steady}${sameTime}`;
}

// The change water is the keeper's usual one: shrimp and some planted
// keepers use remineralised RO on purpose, not tap water.
function changeWaterNote(paramLabel) {
  return `Test that water too, since tap water can carry ${paramLabel} in: if it reads high, water changes cannot take the tank below it.`;
}

function freshNitrateAction(status, tank) {
  const slug = tankSubtype(tank);
  if (status === 'low') {
    return PLANTED.has(slug)
      ? 'Plants use nitrate as fertiliser: add a planted-tank fertiliser with nitrogen in it, following its label. A reading that keeps falling can mean they need more; older leaves turning yellow is a common sign.'
      : `${slug === SHRIMP ? 'Fish and shrimp are' : 'Fish are'} not harmed by low nitrate, so a low reading is nothing to fix for them. If you keep live plants and their older leaves turn yellow, they may be short of it. ${RANGE_TO_ZERO}`;
  }
  const shrimp = slug === SHRIMP ? ' In a shrimp tank, change a little at a time.' : '';
  const fert = PLANTED.has(slug)
    ? ' If you add a fertiliser with nitrogen, check how much it carries.'
    : '';
  return `Water changes bring it down: use your usual change water, dechlorinated if it is tap water, at the tank’s temperature. ${changeWaterNote('nitrate')}${shrimp} Then feed a little less (a single extra pinch a day adds up).${fert}`;
}

function freshPhosphateAction(status, tank) {
  const slug = tankSubtype(tank);
  if (status === 'low') {
    return PLANTED.has(slug)
      ? 'Plants need phosphate: add a planted-tank fertiliser with phosphate in it, following its label. A low reading can hold them back.'
      : `${slug === SHRIMP ? 'Fish and shrimp get' : 'Fish get'} the phosphorus they need from their food, not the water, so a low reading is nothing to fix for them. If you keep live plants, they use it, and a low reading can hold them back. ${RANGE_TO_ZERO}`;
  }
  const shrimp = slug === SHRIMP ? ' In a shrimp tank, change a little at a time.' : '';
  const fert = PLANTED.has(slug)
    ? ' If you add a planted-tank fertiliser, check how much phosphate it carries.'
    : '';
  return `A water change and feeding a little less bring it down: most of it comes from fish food. ${changeWaterNote('phosphate')}${shrimp}${fert}`;
}

// A fish-only saltwater tank has nothing that feeds on nitrate or phosphate,
// so a low reading is not a problem there; the reef low-side copy dosed
// nitrate "for the corals".
function noCoralLowNutrient(paramKey) {
  return paramKey === 'phosphate'
    ? `This is a fish-only tank, and fish get the phosphorus they need from their food, so a low reading is nothing to fix. ${RANGE_TO_ZERO}`
    : `This is a fish-only tank, and fish are not harmed by low nitrate, so a low reading is nothing to fix. ${RANGE_TO_ZERO}`;
}

// A low reading that is nothing to fix still reads LOW against a range that
// starts above zero; this is the way out (the field is "Low" in Tank
// settings, Parameters), and no trend clause ("correct toward the upper
// half of the band") follows it.
const RANGE_TO_ZERO =
  'If nothing in the tank needs it, set its Low to 0 in Tank settings, Parameters, so it stops reading low.';

function nothingToFix(paramKey, status, tank) {
  if (status !== 'low' || (paramKey !== 'nitrate' && paramKey !== 'phosphate')) return false;
  if (isFreshwater(tank)) return !PLANTED.has(tankSubtype(tank));
  return tankSubtype(tank) === 'fowlr';
}

// `status` is the off-band state (ok/high/low). `direction` is the
// optional rate-of-change trend object from trend.js — when present it
// adds a trajectory-aware clause so the advice reflects where the
// parameter is heading, not just where it sits right now.
// `opts.salinityUnit` (ppt | psu | sg) is the keeper's display unit; the
// server's alert copy has no per-device preference and keeps ppt.
// `opts.tempUnit` ('F' | 'C') is the tank's display unit, used by the trend
// clause's rate (the copy's own numbers are per-branch).
export function actionableUpdateFor(
  paramKey,
  latestValue,
  p,
  status,
  tank,
  direction = null,
  opts = {}
) {
  const clause = trendClause(paramKey, status, direction, {
    unit: p?.unit,
    tempUnit: opts.tempUnit,
    salinityUnit: opts.salinityUnit,
  });
  if (paramKey === 'salinity')
    return salinityAction(latestValue, p, status, tank, opts.salinityUnit) + clause;
  // Apex DOS dose context: server attaches `dose_summary[paramKey]` to
  // the tank payload. Falsy on tanks without an Apex / DOS — the dosable
  // branch falls through to the generic "tap DOSE" copy.
  const dose = tank?.dose_summary?.[paramKey] || null;
  // A freshwater tank is never dosed for reef chemistry (the dose route
  // refuses it), so its alk / calcium / magnesium card falls to the generic
  // line below.
  if (DOSABLE.has(paramKey) && !isFreshwater(tank)) {
    return dosableAction(paramKey, status, p, dose, dosesParam(tank, paramKey)) + clause;
  }
  if (nothingToFix(paramKey, status, tank)) {
    return isFreshwater(tank)
      ? paramKey === 'nitrate'
        ? freshNitrateAction(status, tank)
        : freshPhosphateAction(status, tank)
      : noCoralLowNutrient(paramKey);
  }
  if (isFreshwater(tank)) {
    if (paramKey === 'temp') return freshTempAction(status) + clause;
    if (paramKey === 'ph') return freshPhAction(status, tank) + clause;
    if (paramKey === 'nitrate') return freshNitrateAction(status, tank) + clause;
    if (paramKey === 'phosphate') return freshPhosphateAction(status, tank) + clause;
  }
  if (paramKey === 'temp') return tempAction(latestValue, p, status) + clause;
  if (paramKey === 'ph') return phAction(latestValue, p, status, tank) + clause;
  if (paramKey === 'ammonia' || paramKey === 'nitrite')
    return wasteAction(paramKey, status, tank) + clause;
  if (paramKey === 'nitrate') return nitrateAction(latestValue, p, status) + clause;
  if (paramKey === 'phosphate') return phosphateAction(latestValue, p, status) + clause;
  // Final fallback for any param we haven't tailored yet. Deliberately
  // honest about its own genericness — the per-param branches above are
  // where new params should be added, not by re-routing to this.
  const base =
    status === 'low'
      ? 'Look up the usual causes for this parameter and retest in 24 h before making a big move.'
      : 'Look up the usual causes for this parameter and retest in 24 h before making a big move.';
  return base + clause;
}

// Stale-reading concern copy for the Sensei update card. A stale param
// (silent source / frozen value / stuck probe) is a CRITICAL status, not
// a passive "last tested N days ago" note: the value we have can't be
// trusted, and a stuck probe or a forgotten test can hide a real swing
// on a key parameter. `staleParams` is an array of { key, p (param config
// with .label/.unit), reason }.

// Core chemistry — a stale reading on one of these means the keeper is
// flying blind on something that can crash a tank. Used to word the
// concern more urgently and to sort the most important stale param first.
export const CRITICAL_PARAM_KEYS = new Set([
  'alk',
  'calcium',
  'magnesium',
  'salinity',
  'temp',
  'ph',
  'nitrate',
  'phosphate',
]);

function staleReasonClause(reason) {
  if (reason === 'stuck')
    return 'is stuck on the exact same value — the probe or tester has almost certainly failed';
  if (reason === 'frozen') return "hasn't moved in days — the tester has stopped reporting";
  if (reason === 'test_time_unknown')
    return 'came from your Trident, which does not say when it tested — it may be an old result';
  return "hasn't had a fresh, trustworthy test in too long";
}

// Sort the most critical stale param first so the copy leads with the one
// that matters most (a stale alk before a stale nitrate).
function orderStale(staleParams) {
  return [...staleParams].sort(
    (a, b) => (CRITICAL_PARAM_KEYS.has(b.key) ? 1 : 0) - (CRITICAL_PARAM_KEYS.has(a.key) ? 1 : 0)
  );
}

// Prominent sentence appended when stale coexists with an off-band param.
// No longer a throwaway "Also: X is stuck" — it's a full, urgent concern
// in its own right.
export function staleConcernPhrase(staleParams) {
  if (!Array.isArray(staleParams) || staleParams.length === 0) return '';
  const ordered = orderStale(staleParams);
  const first = ordered[0];
  const label = first.p?.label || first.key;
  const blind = CRITICAL_PARAM_KEYS.has(first.key)
    ? ` You may be flying blind on ${label.toLowerCase()} — test it now.`
    : ' Retest it to be sure.';
  const others = ordered.length - 1;
  const more =
    others > 0 ? ` (${others} other reading${others > 1 ? 's are' : ' is'} stale too.)` : '';
  return `Heads up: ${label} ${staleReasonClause(first.reason)}, so Sensei stopped trusting it.${blind}${more}`;
}

// Full concern sentence when stale is the headline (nothing confirmed
// off-band, or a stale critical param leads). Names the param, says why
// it can't be trusted, and what to do — and that it's been dropped from
// the Score so the number can't look better than the tank really is.
export function staleConcernText(staleParams) {
  if (!Array.isArray(staleParams) || staleParams.length === 0) return '';
  const ordered = orderStale(staleParams);
  const first = ordered[0];
  const label = first.p?.label || first.key;
  const why = staleReasonClause(first.reason);
  const blind = CRITICAL_PARAM_KEYS.has(first.key)
    ? ` — you may be flying blind on a key parameter and missing a real swing`
    : '';
  const tail =
    ordered.length > 1
      ? ` (${ordered.length - 1} other reading${ordered.length - 1 > 1 ? 's are' : ' is'} stale too).`
      : '.';
  return `${label} ${why}${blind}. Sensei has dropped it from your Score so the number can't look better than the tank really is — log a fresh test (or check the probe / tester) now to clear it${tail}`;
}
